import { TranslateService } from "@ngx-translate/core";
import { AssociationSource, TypedIdAndIdentity } from "common/associations/association-browser.service";
import { AssociationTarget } from "common/associations/association-list.component";
import { AssociationStore } from "common/associations/association-store.interface";
import { SearchErrorWithResults, SearchQuery } from "common/autoCompleteSearch/search-query";
import { lastValueFrom } from "rxjs";

type TargetWithDependentElementInfo = TypedIdAndIdentity & { dependentElementsContainer?: boolean };

export class AssocSearchResult {
	isNew = false;
	isRemote = false;
	id?: string; // undefined for virtual search results ("Create new ...")
	identity?: string; // undefined for virtual search results ("Create new ...")
	isComment = false;
	name: string;
	label: string;
	type!: { ident: string; subtypeIdent: string | null };
	parentId?: string;
	workspaceId!: string;
	tailored = false;
	dependentElementsContainer = false;
	dependent = false;
	assignableDependentChildSubtypes?: string[];

	constructor(name: string) {
		this.name = name;
		this.label = name;
	}

	static newVirtualElement(
		name: string,
		targetElementType: string,
		targetElementSubtypes: string[],
		dependentElementContainer?: AssocSearchResult,
		targetElementParentId?: string,
		restrictAssociateDependentElementTypes?: string[],
	): AssocSearchResult {
		const firstAssignableRestrictedDependentElementSubtype =
			AssocSearchResult.firstAssignableRestrictedDependentElementSubtype(
				dependentElementContainer,
				restrictAssociateDependentElementTypes,
			);
		const virtualItem = new AssocSearchResult(name);
		virtualItem.type = {
			ident: targetElementType,
			subtypeIdent: firstAssignableRestrictedDependentElementSubtype
				? firstAssignableRestrictedDependentElementSubtype
				: AssocSearchResult.firstSubtypeIdent(targetElementSubtypes),
		};
		virtualItem.isNew = true;
		virtualItem.parentId = targetElementParentId;
		virtualItem.dependent = dependentElementContainer !== undefined;
		return virtualItem;
	}

	static newVirtualCommentOnlyAssociation(
		comment: string,
		targetElementType: string,
		targetElementSubtypes: string[],
	): AssocSearchResult {
		const virtualItem = new AssocSearchResult(comment);
		virtualItem.isComment = true;
		virtualItem.type = {
			ident: targetElementType,
			subtypeIdent: AssocSearchResult.firstSubtypeIdent(targetElementSubtypes),
		};
		virtualItem.isNew = true;
		virtualItem.tailored = false;
		return virtualItem;
	}

	static firstSubtypeIdent(subtypeIdents?: string[]): string | null {
		return !subtypeIdents || subtypeIdents.length === 0 ? null : subtypeIdents[0];
	}

	static firstAssignableRestrictedDependentElementSubtype(
		element: AssocSearchResult | undefined,
		restrictAssociateDependentElementTypes: string[] | undefined,
	): string | undefined {
		if (element?.assignableDependentChildSubtypes && element.assignableDependentChildSubtypes.length > 0) {
			if (restrictAssociateDependentElementTypes) {
				return element.assignableDependentChildSubtypes.find((subtype) =>
					restrictAssociateDependentElementTypes.includes(subtype),
				)!;
			}
			return element.assignableDependentChildSubtypes[0];
		}
		return undefined;
	}
}

export class AssociationTargetSearchQuery<T extends AssociationTarget> implements SearchQuery<AssocSearchResult> {
	constructor(
		private translateService: TranslateService,
		private associationStore: AssociationStore<AssociationSource<T>, T>,
		private targetElementType: string,
		private targetElementSubtypes: string[],
		private targetElementsToIgnore: TargetWithDependentElementInfo[],
		private currentWorkspaceId: string,
		private searchWorkspaceId: string,
		private processVersion: string,
		private isCreateElementAllowed: boolean,
		private isCreateCommentOnlyAssociationAllowed: boolean,
		private allowedDependentElementSubtypes?: string[],
		private dependentElementContainer?: AssocSearchResult,
		private targetElementParentId?: string,
		private targetProcessIds?: string[],
		private changingExistingAssociation: boolean = false,
	) {}

	async run(searchTerm: string, documentTypes?: string[]): Promise<AssocSearchResult[]> {
		try {
			const results = this.dependentElementContainer
				? (
						await this.associationStore.searchDependentProcessElements(
							searchTerm,
							this.targetElementType,
							this.dependentElementContainer.identity!,
							this.targetElementsToIgnore,
							this.currentWorkspaceId,
							this.processVersion,
							this.targetProcessIds,
							documentTypes,
						)
				  )
						// eslint-disable-next-line unicorn/no-await-expression-member -- the result in this optional chain has to be filtered
						.filter(
							(res) =>
								this.allowedDependentElementSubtypes === undefined ||
								(res.type.subtypeIdent && this.allowedDependentElementSubtypes.includes(res.type.subtypeIdent)),
						)
				: await this.associationStore.searchProcessElements(
						searchTerm,
						this.targetElementType,
						this.targetElementSubtypes,
						// An element should be displayed in the search results even when it is already associated, when the
						// following conditions are met:
						// 1. The element can have dependent element children
						// 2. The association widget is not restricted in a way that no dependent element children can be associated
						//    (this can be done by setting the association path in the metamodel)
						this.targetElementsToIgnore.filter(
							(t) => !(t.dependentElementsContainer && allowDependentElements(this.allowedDependentElementSubtypes)),
						),
						this.currentWorkspaceId,
						this.searchWorkspaceId,
						this.processVersion,
						this.targetProcessIds,
						documentTypes,
				  );

			let items: AssocSearchResult[] = [];
			this.addCreateItems(searchTerm, items);
			if (this.dependentElementContainer && searchTerm.length === 0) {
				const shouldBeIgnored = this.targetElementsToIgnore.find((t) => t.id === this.dependentElementContainer?.id);
				if (!shouldBeIgnored) {
					const dependentElementsContainerPlaceHolder = new AssocSearchResult("");
					Object.assign(dependentElementsContainerPlaceHolder, this.dependentElementContainer);
					const label = await lastValueFrom(
						this.translateService.get("process.element.quickAssign.directly", {
							name: this.dependentElementContainer.label,
						}),
					);
					dependentElementsContainerPlaceHolder.name = label;
					dependentElementsContainerPlaceHolder.label = label;
					dependentElementsContainerPlaceHolder.dependentElementsContainer = false; // needs to be false that the element is associated on selection and the flyout is closed
					items.push(dependentElementsContainerPlaceHolder);
				}
			}
			items = [...items, ...this.groupByLocalOrRemote(results)];
			return items;
		} catch (e: unknown) {
			const items: AssocSearchResult[] = [];
			if (
				(this.isCreateElementAllowed || this.isCreateCommentOnlyAssociationAllowed) &&
				!this.changingExistingAssociation
			) {
				this.addCreateItems(searchTerm, items);
				throw new SearchErrorWithResults(items, e);
			}
			throw e;
		}
	}

	private addCreateItems(searchTerm: string, items: AssocSearchResult[]): void {
		if (this.isCreateElementAllowed && searchTerm.length > 0) {
			items.push(
				AssocSearchResult.newVirtualElement(
					searchTerm,
					this.targetElementType,
					this.targetElementSubtypes,
					this.dependentElementContainer,
					this.targetElementParentId,
					this.allowedDependentElementSubtypes,
				),
			);
		}
		if (this.isCreateCommentOnlyAssociationAllowed && searchTerm.length > 0 && !this.dependentElementContainer) {
			items.push(
				AssocSearchResult.newVirtualCommentOnlyAssociation(
					searchTerm,
					this.targetElementType,
					this.targetElementSubtypes,
				),
			);
		}
	}

	groupByLocalOrRemote(results: AssocSearchResult[]): AssocSearchResult[] {
		const local = results
			.filter((result) => result.workspaceId === this.currentWorkspaceId)
			.map((result) => {
				return { ...result, isRemote: false };
			});
		const remote = results
			.filter((result) => result.workspaceId !== this.currentWorkspaceId)
			.map((result) => {
				return { ...result, isRemote: true };
			});
		return [...local, ...remote];
	}
}

function allowDependentElements(allowedDependentElementSubtypes: string[] | undefined): boolean {
	if (!allowedDependentElementSubtypes) {
		// Anything is allowed
		return true;
	}
	return allowedDependentElementSubtypes.length > 0;
}
